import moment from "moment-timezone";
import "./report-list.less";
import template from "./report-list.html";
import {
	debounce,
	filter,
	find,
	some,
	uniq
} from "lodash";
import {
	EVENT_NEW_REPORTING_ENTITY,
	FIELD_KEYS,
	FIELD_TEMPLATES,
	TIME_DISPLAY_OPTIONS,
	FORM_TIMEZONE_TYPES,
	EVENT_RESET_REPORTING_ENTITY,
	REPORT_STATES
} from "constants.js";
import { pluralize } from "humanize-plus";
import {
	FORM_EXPORT_REPORTS,
	EDIT_REPORT,
	NEW_REPORT,
	VIEW_REPORTS
} from "routes.js";
import { ROLE_TYPES } from "config/permissions.js";
import {
	hasHadState, 
	isValueNullish 
} from "../../utils/";
import { entityEffectiveStatus } from "../../utils/effectiveRanges";
import { FORM_REPORT_QUERY } from "../../routes";
import { triggerFileDownload } from "../../utils/files";
import hotkeyConfigs from "../../services/hotkeyConfig";

function controller(
	$scope,
	$state,
	$location,
	$logger,
	$sce,
	appState,
	api,
	eventBus,
	loadingService,
	toastr,
	timezoneService,
	entityManager,
	$userProfile,
	fieldValueHelper,
	hotkeys
) {
	const ctrl = this;

	ctrl.loadReports = debounce(loadReports, 500);

	ctrl.table = {
		data: [],
		actions: {},
		sort: {
			predicate: "Number",
			direction: "desc"
		},
		pagination: {
			currentPage: parseInt($state.params.page) || 1,
			itemsPerPage: $state.params.itemsPerPage || $userProfile.getUserProfile().pagePreference || "10"
		}
	};
	ctrl.paginatorOptions = {};

	ctrl.tableWidthCss = "col-sm-9";

	ctrl.$onInit = onInit;
	ctrl.open = open;
	ctrl.download = download;
	ctrl.viewSelectedReports = viewSelectedReports;
	ctrl.selectReports = selectReports;
	ctrl.clearSelection = clearSelection;
	ctrl.setSelected = setSelected;
	ctrl.onCollapse = onCollapse;
	ctrl.getEditButtonIcon = getEditButtonIcon;
	ctrl.getExtraData = getExtraData;
	ctrl.updateDateFilter = updateDateFilter;
	ctrl.getDateTime = getDateTime;
	ctrl.updateUserPreference = updateUserPreference;

	ctrl.showFriendlyEntitySelector = false;
	ctrl.primaryReportingEntity = { Entities: [] };

	ctrl.NEW_REPORT = NEW_REPORT;
	ctrl.EDIT_REPORT = EDIT_REPORT;
	ctrl.selected = [];

	const buildReportListEvent = $logger.buildWebEvent("reportList");

	function onInit() {
		ctrl.promiseTracker = loadingService.getTracker();
		ctrl.form = appState.forms.selected;
		ctrl.permissions = appState.permissions;
		ctrl.isSysAdmin = appState.operatingUserRole === "SYSADMIN" || appState.operatingUserRole === "AGENT";

		ctrl.timeDisplay = $userProfile.getUserProfile().timeDisplay;
		ctrl.timeFormat = this.timeDisplay === TIME_DISPLAY_OPTIONS.TWELVE_HOUR ? "MM-DD-YYYY hh:mm A" : "MM-DD-YYYY HH:mm";
		ctrl.dateOnlyFormat = "MM-DD-YYYY";

		ctrl.statusOptions = [
			{ key: null, display: "All" },
			{ key: REPORT_STATES.deleted.code, display: REPORT_STATES.deleted.display },
			{ key: REPORT_STATES.pending.code, display: REPORT_STATES.pending.display },
			{ key: REPORT_STATES.submitted.code, display: REPORT_STATES.submitted.display },
			{ key: REPORT_STATES.declared.code, display: REPORT_STATES.declared.display }
		];

		ctrl.licenses = [{ key: null, display: "All" }];

		eventBus.register(EVENT_NEW_REPORTING_ENTITY, updateReportSearch, $scope);

		hotkeys.bindTo($scope).add(
			hotkeyConfigs.REPORT_LIST.VIEW_SELECTED_REPORTS(
				null,
				() => ctrl.viewSelectedReports()
			)
		);
		
		setDefaultFilters();

		$scope.$watch(() => ctrl.filter, updateUrl, true);
	}

	function setDefaultFilters() {
		const dateStart = $state.params.start;
		const dateEnd = $state.params.end;
		const statusKey = $state.params.status;
		const licenseIds = $state.params.entityIds;
		const beginNumber = $state.params.beginNumber;
		const endNumber = $state.params.endNumber;

		const start = dateStart ? moment(dateStart, "YYYY/MM/DD") : moment().startOf("month");
		const end = dateEnd ? moment(dateEnd, "YYYY/MM/DD") : moment().endOf("month");
		const status = statusKey ? ctrl.statusOptions.find(x => x.key === statusKey) : ctrl.statusOptions[0];

		const firstLicenseId = licenseIds && licenseIds.length > 0 ? parseInt(licenseIds[0]) : null;

		ctrl.filter = {
			startDate: start.toDate(),
			endDate: end.toDate(),
			license: licenseIds && licenseIds.length === 1 ? { key: firstLicenseId } : { key: null, display: "All" },
			status: status,
			beginNumber: beginNumber || null,
			endNumber: endNumber || null
		};

		!!licenseIds?.length && entityManager.selectEntity(firstLicenseId, ctrl.filter.license.key === null);
	}

	function updateReportingEntitySelector(friendly = ctrl.primaryReportingEntity) {
		ctrl.primaryReportingEntity = friendly;

		if(!ctrl.primaryReportingEntity) {
			ctrl.licenses = [{ key: null, display: "All" }];
			ctrl.filter.license.key = null;
		}

		else if(!ctrl.primaryReportingEntity.isSelf) {
			const validEntities = ctrl.primaryReportingEntity.Entities.filter((entity) => {
				const start = moment(ctrl.filter.startDate);
				const end = moment(ctrl.filter.endDate);
				const effectiveAsOf = { fromInclusive: start, toExclusive: end, allowPartial: true };

				return entityEffectiveStatus(entity, effectiveAsOf).isEither;
			});

			ctrl.licenses = [
				{ key: null, display: "All" },
				...validEntities.map(x => ({ key: x.Entity.Id, display: x.Entity.Code }))
			];

			const selectedLicenseIsNotValid = ctrl.licenses.map(l => l.key && l.key.toString()).indexOf(ctrl.filter.license.key) < 0;
			if(!ctrl.filter.license || selectedLicenseIsNotValid) {
				ctrl.filter.license.key = ctrl.licenses.length === 2 ? ctrl.licenses[1].key : null;
			}

			ctrl.showFriendlyEntitySelector = ctrl.licenses.length > 2;

			if(ctrl.showFriendlyEntitySelector) {
				ctrl.primaryAssociatedEntityName =
					ctrl.primaryReportingEntity && ctrl.primaryReportingEntity.Entities && ctrl.primaryReportingEntity.Entities[0] && ctrl.primaryReportingEntity.Entities[0].Entity
						? ctrl.primaryReportingEntity.Entities[0].Entity.Type.Name
						: null;
			}
		}
		else {
			ctrl.licenses = [
				{ key: null, display: "All" },
				{ key: ctrl.primaryReportingEntity.Id, display: ctrl.primaryReportingEntity.Name || ctrl.primaryReportingEntity.Code }
			];

			ctrl.filter.license.key = ctrl.primaryReportingEntity.Id;
		}
		if(ctrl.licenses.length < 2) {
			eventBus.trigger(EVENT_RESET_REPORTING_ENTITY);
		}
		else {
			updateUrl();
		}
	}

	function updateReportSearch() {
		ctrl.licenses = [];
		ctrl.showFriendlyEntitySelector = false;

		updateReportingEntitySelector(entityManager.getSelectedEntity().friendly);
	}

	function download() {
		const label = pluralize(ctrl.selected.length, ctrl.form.nomenclature.PositiveReport);

		$logger.event(buildReportListEvent("export")("csv"), { length: ctrl.selected.length });

		if(ctrl.selected.length) {
			const params = { Ids: ctrl.selected };

			const promise = api
				.post(FORM_EXPORT_REPORTS(ctrl.form.Id), params, { headers: { "Accept": "text/csv" } })
				.then(response => {
					const fileNameToSaveAs = `VESL_Reports_${moment().format("MM-DD-YYYY-HH-mm-ss")}.csv`;
					
					triggerFileDownload(response, fileNameToSaveAs, "text/csv");
				})
				.catch(err => {
					$logger.exception(err, "Failure occurred while exporting reports");
				});

			loadingService.watch(promise, "Downloading...");
		} else {
			toastr.warning(`Please select ${label} to download.`);
		}
	}

	function viewSelectedReports() {
		const label = pluralize(ctrl.selected.length, ctrl.form.nomenclature.PositiveReport);

		$logger.event(buildReportListEvent("view")("selected"), { length: ctrl.selected.length });

		if(ctrl.selected.length) {
			$state.go(VIEW_REPORTS, { ids: ctrl.selected.slice() });
			return;
		}

		toastr.warning(`Please select ${label} to view.`);
	}

	function clearSelection() {
		ctrl.selectAll = false;
		selectReports();
	}

	function selectReports() {
		if(ctrl.table.data.length < 1) {
			ctrl.selectAll = false;
		} else {
			ctrl.table.data.forEach(r => {
				if(ctrl.isSysAdmin || (r.Submitted && !r.IsDeleted)) {
					r.selected = ctrl.selectAll;
				}
			});
			setSelected();
		}
	}

	function setSelected() {
		let anyNotSelected;

		ctrl.selected = filter(ctrl.table.data, item => {
			const isSelectable = ctrl.isSysAdmin || (item.Submitted && !item.IsDeleted);
			if(!item.selected && isSelectable) {
				anyNotSelected = true;
			}
			return item.selected === true;
		}).map(r => r.Id);

		ctrl.selectAll = (ctrl.selectAll || !anyNotSelected) && !anyNotSelected;
	}

	function updateDateFilter(startDate, endDate) {
		if(startDate || endDate) {
			ctrl.filter.startDate = startDate || ctrl.filter.startDate;
			ctrl.filter.endDate = endDate || ctrl.filter.endDate;

			const endDateParam = endDate && moment(ctrl.filter.endDate).format();
			const startDateParam = startDate && moment(ctrl.filter.startDate).format();

			$location
				.search("start", startDateParam)
				.search("end", endDateParam)
				.replace();

			updateReportingEntitySelector();
		}
	}

	function getDateTime(dateData) {
		let thisMoment = dateData && moment(dateData);

		if(!thisMoment.isValid() && dateData) {
			thisMoment = moment(dateData, ctrl.timeFormat);
		}

		if(ctrl.primaryDateColumn.Template === FIELD_TEMPLATES.DateTime) {
			return this.timeDisplay === TIME_DISPLAY_OPTIONS.TWELVE_HOUR ? thisMoment.format("MM-DD-YYYY hh:mm A") : thisMoment.format("MM-DD-YYYY HH:mm");
		}

		return thisMoment?.format(ctrl.dateOnlyFormat);
	}

	function loadReports() {
		ctrl.selected = [];
		ctrl.selectAll = false;

		const selectedEntity = entityManager.getSelectedEntity();

		if(selectedEntity && (selectedEntity.primary || selectedEntity.friendly)) {
			const isFriendlySelf = entityManager.isEntitySelf(selectedEntity.friendly);
			const primaryEntity = (selectedEntity.primary.Id ? selectedEntity.primary : null) || (isFriendlySelf ? selectedEntity.friendly : null) || {};

			let entityId,
				entityIds = [];

			if(ctrl.showFriendlyEntitySelector) {
				entityIds = ctrl.filter.license && ctrl.filter.license.key ? [parseInt(ctrl.filter.license.key)] : ctrl.licenses.slice(1).map(x => x.key);
			}
			else {
				entityId = ctrl.licenses && ctrl.licenses.length === 2 && ctrl.licenses[1].key;
			}

			if(ctrl.filter.startDate && ctrl.filter.endDate && (entityId || (entityIds && entityIds.length > 0))) {
				const params = buildReportsRequest(ctrl.filter.startDate, ctrl.filter.endDate, entityId, entityIds, ctrl.filter.status.key);

				const url = FORM_REPORT_QUERY(ctrl.form.Id, params);
				const promise = api.get(url).then(response => {
					const extraColumns = ctrl.form.Fields.filter(field => {
						const user = $userProfile.getUser();
						const isUser = user.roles.indexOf(ROLE_TYPES.user) > -1;

						if(isUser && field.Key.Code === FIELD_KEYS.tripType) field.IsIncludedInHeaderSummary = false;

						return field.IsIncludedInHeaderSummary === true;
					}).map(field => {
						const fieldInfo = { 
							name: field.Name, 
							type: field.Template,
							fieldId: field.Id
						};

						if(field.Template === FIELD_TEMPLATES.Number) {
							fieldInfo["rounding"] = field.RoundingPoint;
						}

						return fieldInfo;
					});

					const dateColumns = extraColumns.filter(field => {
						const rowField = find(ctrl.form.Fields, formField => formField.Name === field.name);
						return rowField.Template === FIELD_TEMPLATES.DateTime;
					});

					ctrl.primaryDateColumn = find(ctrl.form.Fields, field => {
						return !!field.IsPrimary && (field.Template === FIELD_TEMPLATES.Date || field.Template === FIELD_TEMPLATES.DateTime || field.Template === FIELD_TEMPLATES.YearMonth);
					});

					ctrl.extraColumns = extraColumns;

					const timeZoneMode = ctrl.form.TimeMode;
					let timeZoneCode = null;

					if(timeZoneMode.Code === FORM_TIMEZONE_TYPES.ByEntity) {
						if(primaryEntity.Id === undefined) {
							const entitiesTimeZones = selectedEntity.friendly.Entities
								.filter(e => entityIds.indexOf(e.Entity.Id) > -1 && !isValueNullish(e.Entity.TimeZone?.Code))
								.map(e => e.Entity.TimeZone?.Code);
							const uniqueZones = uniq(entitiesTimeZones);

							if(uniqueZones.length === 1) {
								timeZoneCode = uniqueZones[0];
								ctrl.timeZoneAbbr = timezoneService.getTimezoneByIANACode(timeZoneCode).name;
							}
							else {
								ctrl.timeZoneAbbr = -1;
							}
						}
						else {
							timeZoneCode = primaryEntity.TimeZone?.Code ?? selectedEntity.friendly?.TimeZone?.Code;
							ctrl.timeZoneAbbr = timezoneService.getTimezoneByIANACode(timeZoneCode).name;
						}
					} else if(timeZoneMode.Code === FORM_TIMEZONE_TYPES.ByForm) {
						timeZoneCode = ctrl.form.TimeZone.Code;
						ctrl.timeZoneAbbr = timezoneService.getTimezoneByIANACode(timeZoneCode).name;
					}
					
					ctrl.table.data = response.map(row => {
						if(ctrl.timeZoneAbbr === -1) {
							const currentEntity = find(selectedEntity.friendly.Entities, e => e.Entity.Id === row.EntityId);
							timeZoneCode = currentEntity.Entity.TimeZone.Code;
						}

						const dateTzMoment = row.Date && !!timeZoneCode && moment(row.Date).tz(timeZoneCode);
						const lmTzMoment = !!timeZoneCode && moment(row.LastModified).tz(timeZoneCode);

						if(ctrl.primaryDateColumn.Template === FIELD_TEMPLATES.DateTime) {
							row.Date = dateTzMoment;
						} else {
							row.Date = row.Date && moment(row.Date.substring(0, row.Date.indexOf("T")));
						}

						row.LastModified = lmTzMoment;
						const license = find(ctrl.licenses, l => l.key === row.EntityId);
						row.EntityName = license && license.display;

						if(ctrl.extraColumns.length === 1) {
							if(row[ctrl.extraColumns[0].name]) {
								row.singleExtraDataColumn = row[ctrl.extraColumns[0].name];
							} 
							else {
								row.singleExtraDataColumn = "--";
							}
						}

						for(let i = 0; i < dateColumns.length; i++) {
							if(!row[dateColumns[i].name]) 
								continue;
								
							let momented = moment(row[dateColumns[i].name]);
							if(!momented.isValid()) {
								momented = moment(row[dateColumns[i].name], "YYYY-MM-DD HH:mm:ss.SSSSSSS ZZ");
							}
							const TZd = !!timeZoneCode && momented.tz(timeZoneCode);
							const overridden = TZd;

							row[dateColumns[i].name] = overridden.format(ctrl.timeFormat);
						}

						row.editable = !ctrl.permissions.Reporting.IsDisabled && ctrl.permissions.Reporting.Actions.CanEdit && !row.Submitted && !row.IsDeleted;
						if(!ctrl.permissions.Reporting.Actions.CanAmend && !!hasHadState(REPORT_STATES.submitted.code, row)) {
							row.editable = false;
						}
						return row;
					});

					ctrl.fishermanColumn = some(ctrl.table.data, r => r.Fisherman);
					ctrl.sellerColumn = some(ctrl.table.data, r => r["Selling License"]);
					ctrl.reportsLabel = `${ctrl.table.data.length} ${pluralize(ctrl.table.data.length, ctrl.form.nomenclature.PositiveReport)}`;
					ctrl.paginatorOptions = {
						...ctrl.paginatorOptions, 
						totalItems: ctrl.table.data.length
					};
				});

				ctrl.promiseTracker.addPromise(promise);
			}
			return;
		}

		ctrl.table.data.length = 0;
		ctrl.paginatorOptions = {
			...ctrl.paginatorOptions, 
			totalItems: 0
		};
	}

	function onCollapse() {
		ctrl.tableWidthCss = ctrl.isCollapsed ? "col-sm-12" : "col-sm-9";
	}

	function getEditButtonIcon(item) {
		return item.editable ? "zmdi zmdi-edit" : "zmdi zmdi-eye";
	}

	function getExtraData(reportHeader) {
		if(ctrl.extraColumns?.length === 1) {
			const fieldValue = getExtraDataValue(reportHeader, ctrl.extraColumns[0]);
			if(!isValueNullish(fieldValue)) {
				return $sce.trustAsHtml(fieldValue);
			} 
			else {
				return $sce.trustAsHtml("<div>--</div>");
			}
		}

		const extraData = ctrl.extraColumns.map(col => extractColumnDataHtml(col, reportHeader));

		const dataBlock = `<div>
			${extraData.join("")}
		</div>`;

		return $sce.trustAsHtml(dataBlock);
	}

	function extractColumnDataHtml(col, reportHeader) {
		const rawItemData = getExtraDataValue(reportHeader, col);
		if(rawItemData === undefined) {
			return `<small><b>${col.name}</b>: --</small>`;
		} 
		else {
			return `<small><b>${col.name}</b>: ${rawItemData}</small>`;
		}
	}

	function getExtraDataValue(reportHeader, col) {
		let fieldValue;
		if(reportHeader.Fields) {
			const reportValues = filter(reportHeader.Fields, field => field.FieldId === col.fieldId);
			const fieldConfig = find(ctrl.form.Fields, field => field.Id === col.fieldId);
			
			fieldValue = fieldValueHelper.formatFieldValue(fieldConfig, reportValues);
			
			if(col.type === FIELD_TEMPLATES.Number) {
				const unit = fieldValueHelper.getNumberUnit(fieldConfig, reportValues);
				if(unit && unit !== "#") {
					if(unit === "$") {
						fieldValue = `${unit}${fieldValue}`;
					}
					else {
						fieldValue = `${fieldValue} ${unit}`;
					}
				}
			}
		}
		else {
			fieldValue = reportHeader[col.name];

			if(col.type === FIELD_TEMPLATES.Date) {
				let autoParseAttempt = moment(fieldValue).format("MM-DD-YYYY");
				if(autoParseAttempt === "Invalid date") {
					autoParseAttempt = moment(fieldValue, "YYYY-MM-DD HH:mm:ss.SSSSSSS ZZ").format("MM-DD-YYYY");
				}

				fieldValue = autoParseAttempt;
			} 
			else if(col.type === FIELD_TEMPLATES.Number && fieldValue !== undefined) {
				const indexOfUnit = fieldValue.indexOf(" ");
				const itemUnit = fieldValue.substring(indexOfUnit + 1);

				fieldValue = fieldValue.slice(0, indexOfUnit);

				const rawItemNumber = parseFloat(fieldValue);

				fieldValue = rawItemNumber.toFixed(col.rounding);

				if(itemUnit !== "#") {
					fieldValue = `${fieldValue} ${itemUnit}`;
				}
			}
		}
		return fieldValue;
	}

	function buildReportsRequest(start, end, entityId, entityIds, status) {
		const params = {
			BeginDate: moment(start).format("YYYY/MM/DD"),
			EndDate: moment(end).format("YYYY/MM/DD")
		};
		if(!ctrl.showFriendlyEntitySelector) {
			params["EntityId"] = entityId;
		} else {
			params["EntityIds"] = entityIds;
		}
		if(status) {
			params["Status"] = status;
		}
		if(ctrl.filter.beginNumber) {
			params["beginNumber"] = ctrl.filter.beginNumber;
		}
		if(ctrl.filter.endNumber) {
			params["endNumber"] = ctrl.filter.endNumber;
		}
		return params;
	}

	function updateUrl() {
		const endDateParam = ctrl.filter.endDate && moment(ctrl.filter.endDate).format();
		const startDateParam = ctrl.filter.startDate && moment(ctrl.filter.startDate).format();
		const entityIdsParam = ctrl.filter.license && ctrl.filter.license.key ? [ctrl.filter.license.key] : ctrl.licenses.slice(1).map(x => x.key);

		$location
			.search("start", startDateParam || null)
			.search("end", endDateParam || null)
			.search("status", ctrl.filter.status.key || null)
			.search("entityIds", entityIdsParam?.length ? entityIdsParam : null)
			.search("beginNumber", ctrl.filter.beginNumber || null)
			.search("endNumber", ctrl.filter.endNumber || null)
			.replace();

		ctrl.loadReports();
	}

	function updateUserPreference(value) {
		const currentProfile = $userProfile.getUserProfile();
		currentProfile.pagePreference = value;
		$userProfile.setUserProfile(currentProfile);
	}
}

export default angular
	.module("vesl.screens.report-list", [])
	.component("reportList", { template, controller }).name;
